import { useEffect, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';

// Hooks
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

// Components
import Button from '@/components/base/Button';

type LoadMoreButtonProps<TDataPromise> = React.ComponentProps<typeof Button> & {
    loadMoreItems: () => Promise<TDataPromise>;
    isLoadingMore: boolean;
    isAutoLoadEnabled?: boolean;
};

const LoadMoreButton = <TDataPromise,>({
    loadMoreItems,
    isLoadingMore,
    isAutoLoadEnabled = true,
    ...restProps
}: LoadMoreButtonProps<TDataPromise>) => {
    const { t } = useTranslation('commons');
    const timeoutId = useRef<NodeJS.Timeout | null>(null);
    const loadMoreButtonRef = useRef<HTMLButtonElement | null>(null);
    const loadMoreInViewPort = useIntersectionObserver(loadMoreButtonRef);

    useEffect(() => {
        clearTimeout(timeoutId.current);

        if (isAutoLoadEnabled && !isLoadingMore && loadMoreInViewPort) {
            timeoutId.current = setTimeout(() => {
                loadMoreItems();
            }, 500);
        }

        return () => clearTimeout(timeoutId.current);
    }, [isLoadingMore, loadMoreInViewPort, loadMoreItems, isAutoLoadEnabled]);

    return (
        <Button
            ref={loadMoreButtonRef}
            isLoading={isLoadingMore}
            onClick={loadMoreItems}
            {...restProps}>
            {t('load_more')}
        </Button>
    );
};

export default LoadMoreButton;
